import { isDate, isString } from 'lodash';
import { DateTime, DateTimeUnit, DurationLike, SystemZone, Zone } from 'luxon';

import { Maybe } from './globals';

export type DateLike = DateTime | Date | NovaDate | string;

export class NovaDateInvalidValueError extends Error {
  constructor(public readonly value: DateLike) {
    const message = `NovaDate: invalid value for date: ${value}`;
    super(message);
    this.message = message;
    this.name = this.constructor.name;
    Error.captureStackTrace(this, this.constructor);
  }
}

/** Thrown when the underlying Luxon DateTime is invalid (e.g. unsupported zone after {@link NovaDate.setTz}). */
export class NovaDateInvalidStateError extends Error {
  constructor(message: string) {
    super(message);
    this.message = message;
    this.name = this.constructor.name;
    Error.captureStackTrace(this, this.constructor);
  }
}

export class NovaDate {
  private readonly strFormats = [DateTime.fromISO, DateTime.fromRFC2822, DateTime.fromSQL] as const;

  constructor(private readonly value: DateLike) {}

  public static tzNow(): NovaDate {
    return new NovaDate(DateTime.now().toUTC());
  }

  private stringToDateTime(str: string, shouldThrow?: true): DateTime;
  private stringToDateTime(str: string, shouldThrow: false): Maybe<DateTime>;
  private stringToDateTime(str: string, shouldThrow = true): Maybe<DateTime> {
    const dt = this.strFormats.map(t => t(str)).find(t => t.isValid);
    if (!dt && shouldThrow) {
      throw new NovaDateInvalidValueError(str);
    }
    return dt;
  }

  public toJSDate(): Date {
    if (DateTime.isDateTime(this.value)) {
      return this.value.toJSDate();
    }
    if (isDate(this.value)) {
      return this.value;
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value).toJSDate();
    }
    if (this.value instanceof NovaDate) {
      return this.value.toJSDate();
    }
    throw new NovaDateInvalidValueError(this.value);
  }

  public toISOString(): string {
    if (DateTime.isDateTime(this.value)) {
      return this.value.toISO();
    }
    if (isDate(this.value)) {
      return this.value.toISOString();
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value).toISO();
    }
    if (this.value instanceof NovaDate) {
      return this.value.toISOString();
    }
    throw new NovaDateInvalidValueError(this.value);
  }

  public toISODateString(): string {
    return this.truncate('day').format('yyyy-MM-dd');
  }

  public toDateTime(): DateTime {
    if (DateTime.isDateTime(this.value)) {
      return this.value;
    }
    if (isDate(this.value)) {
      return DateTime.fromJSDate(this.value);
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value);
    }
    if (this.value instanceof NovaDate) {
      return this.value.toDateTime();
    }
    throw new NovaDateInvalidValueError(this.value);
  }

  public truncate(unit: DateTimeUnit): NovaDate {
    return new NovaDate(this.toDateTime().startOf(unit));
  }

  /**
   * Returns a new NovaDate with the given duration added.
   *
   * @example ```typescript
   * new NovaDate('2025-07-17T00:00:00.000Z').plus({ days: 7 }).toISOString();
   * // '2025-07-24T00:00:00.000Z'
   * ```
   */
  public plus(duration: DurationLike): NovaDate {
    return new NovaDate(this.toDateTime().plus(duration));
  }

  /**
   * Returns a new NovaDate with the given duration subtracted.
   *
   * @example ```typescript
   * new NovaDate('2025-07-17T00:00:00.000Z').minus({ days: 7 }).toISOString();
   * // '2025-07-10T00:00:00.000Z'
   * ```
   */
  public minus(duration: DurationLike): NovaDate {
    return new NovaDate(this.toDateTime().minus(duration));
  }

  /**
   * Formats the DateLike value to `fmt`.
   * If a timezone is specified, it will set the timezone before formatting.
   * If no timezone is specified, but the DateLike value has a timezone
   * defined, it will convert to that timezone before formatting.
   * If no timezone is specified and the DateLike has no timezone or is using
   * the `SystemZone` tz, then it will convert to `UTC` before converting.
   * The locale set via `setLocale` will be used for formatting if available.
   */
  public format(fmt: string, opts: { tz?: string | Zone; locale?: string } = {}): string {
    const dt = this.toDateTime();
    const defaultTz = dt.zone instanceof SystemZone ? 'utc' : dt.zone;
    const dtWithTz = dt.setZone(opts?.tz ?? defaultTz);
    return dtWithTz.toFormat(fmt, opts?.locale ? { locale: opts.locale } : {});
  }

  /**
   * Set the timezone to `tz` while keeping the global time.
   *
   * @example
   * const [year, month, day, hour, minute] = [2025, 7, 17, 11, 29];
   * const dt = DateTime.fromObject({ year, month, day, hour, minute }, { zone: 'utc' });
   * const brt = FixedOffsetZone.parseSpecifier('UTC-3');
   * new NovaDate(dt).setTz(brt).toISOString() // 2025-07-17T08:29:00.000-03:00
   */
  public setTz(tz: string | Zone): NovaDate {
    return new NovaDate(this.toDateTime().setZone(tz));
  }

  /**
   * Replaces the timezone to `tz` while keeping the local time.
   *
   * @example
   * const [year, month, day, hour, minute] = [2025, 7, 17, 11, 29];
   * const dt = DateTime.fromObject({ year, month, day, hour, minute }, { zone: 'utc' });
   * const brt = FixedOffsetZone.parseSpecifier('UTC-3');
   * new NovaDate(dt).setTz(brt).toISOString() // 2025-07-17T11:29:00.000-03:00
   */
  public replaceTz(tz: string | Zone): NovaDate {
    return new NovaDate(this.toDateTime().setZone(tz, { keepLocalTime: true }));
  }

  /**
   * Set the locale to `locale` for formatting purposes.
   *
   * @example
   * const [year, month, day] = [2025, 7, 17];
   * const dt = DateTime.fromObject({ year, month, day }, { zone: 'utc' });
   * new NovaDate(dt).setLocale('pt-BR').format('MMMM') // 'julho'
   * new NovaDate(dt).setLocale('en').format('MMMM') // 'July'
   */
  public setLocale(locale: string): NovaDate {
    return new NovaDate(this.toDateTime().setLocale(locale));
  }

  public valueOf(): number {
    return this.toDateTime().valueOf();
  }

  /**
   * Compare if two dates represent the same moment regardless of timezone.
   *
   * @param other The NovaDate instance to compare with.
   * @returns True if both instances represent the same date and time, false otherwise.
   *
   * @example ```typescript
   * const dtA = new NovaDate('2025-10-09T14:07:00.000+00:00');
   * const dtB = new NovaDate('2025-10-09T14:07:00.000Z');
   * const dtC = new NovaDate('2025-10-09T14:07:00.000+01:00');
   * console.log(dtA.equals(dtB)); // true
   * console.log(dtA.equals(dtC)); // false
   * ```
   */
  public equals(other: Maybe<NovaDate>): boolean {
    return Number(this) === Number(other);
  }

  /**
   * Check if the date value is valid.
   *
   * @returns Whether the date value is valid.
   *
   * @example ```typescript
   * const dt = new NovaDate('2025-10-09T14:07:00.000+00:00');
   * console.log(dt.isValid()); // true
   * ```
   */
  public isValid(): boolean {
    if (DateTime.isDateTime(this.value)) {
      return this.value.isValid;
    }
    if (isDate(this.value)) {
      return !isNaN(this.value.getTime());
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value, false)?.isValid ?? false;
    }
    if (this.value instanceof NovaDate) {
      return this.value.isValid();
    }
    return false;
  }

  /**
   * Throws if this instance is not valid (e.g. unparseable input or invalid zone after {@link setTz}).
   * Unparseable strings throw {@link NovaDateInvalidValueError} from {@link toDateTime}.
   * Parsed-but-invalid DateTime (typical: bad IANA zone) throws {@link NovaDateInvalidStateError}.
   *
   * @returns `this` for chaining
   * @throws {NovaDateInvalidValueError} when the value cannot be converted to a DateTime
   * @throws {NovaDateInvalidStateError} when the underlying DateTime is invalid
   */
  public ensureValid(): NovaDate {
    if (this.isValid()) {
      return this;
    }
    const dt = this.toDateTime();
    if (!dt.isValid) {
      throw new NovaDateInvalidStateError(
        dt.invalidExplanation ?? dt.invalidReason ?? 'invalid DateTime'
      );
    }
    throw new NovaDateInvalidStateError('invalid date');
  }

  // these are magic methods the language uses to convert an object to string {{{
  public toJSON(): string {
    return this.toString();
  }

  public toString(): string {
    return this.toISOString();
  }

  public [Symbol.toPrimitive](hint: 'string' | 'default'): string;
  public [Symbol.toPrimitive](hint: 'number'): number;
  public [Symbol.toPrimitive](hint: 'string' | 'number' | 'default'): string | number {
    if (hint === 'number') {
      return this.valueOf();
    }
    return this.toISOString();
  }
  // }}}
}
